import React, { useEffect } from "react";
import { FormattedMessage, injectIntl, type WrappedComponentProps } from "react-intl";
import { Field, FieldArray, reduxForm, type WrappedFieldArrayProps, type WrappedFieldProps } from "redux-form";

import { Minus, Plus } from "~/common/redesign-v1/icons";
import { Input } from "~/components/ui/input";

function LinksCT() {
  return <FieldArray name="links" component={RenderFields} />;
}

type TLink = { link?: string };
export type ReduxFormLinks = { links: TLink[] };

const initialValues: ReduxFormLinks = { links: [{}] };
export default reduxForm({ form: "links", initialValues })(LinksCT);

/** Ensure at least one CT-link row — empty FieldArray renders nothing (looks like the field "vanished"). */
function RenderFields({ fields }: WrappedFieldArrayProps) {
  useEffect(() => {
    if (fields.length === 0) {
      fields.push({});
    }
  }, [fields, fields.length]);

  return (
    <div className="rd-ct-links">
      {fields.map((name, index) => (
        <LinkRow key={name} name={name} index={index} fields={fields} />
      ))}
    </div>
  );
}

type LinkRowProps = {
  name: string;
  index: number;
  fields: WrappedFieldArrayProps<TLink>["fields"];
};

const LinkRow = injectIntl(function LinkRow({ name, index, fields, intl }: LinkRowProps & WrappedComponentProps) {
  const removeField = () => {
    if (fields.length > 1) {
      fields.remove(index);
    }
  };

  const addField = () => {
    fields.push({});
  };

  return (
    <div className="rd-ct-links__row">
      <Field
        component={renderFieldValidation}
        name={`${name}.link`}
        className="form-control"
        type="text"
        validate={[validateLink]}
        normalize={(v: string) => v.trim()}
        id={`${name}.link`}
        placeholder={intl.formatMessage({
          id: "redesign.patient.form.ct.link_placeholder",
          defaultMessage: "Enter a CT link",
        })}
      />
      <label className="control-label rd-ct-links__legacy-label" id="validation-link" style={{ fontSize: "12px", fontStyle: "italic" }} htmlFor={`${name}.link`}>
        (<FormattedMessage id="ADD_LINK_TO_CT" />)
      </label>

      <div className="rd-ct-links__steppers">
        <button className="btn btn-danger rd-ct-links__stepper" type="button" onClick={removeField}>
          <Minus className="tw-size-4" aria-hidden="true" />
          <span className="tw-sr-only">Удалить ссылку</span>
        </button>

        <button className="btn btn-success rd-ct-links__stepper" type="button" onClick={addField}>
          <Plus className="tw-size-4" aria-hidden="true" />
          <span className="tw-sr-only">Добавить ссылку</span>
        </button>
      </div>
    </div>
  );
});

function renderFieldValidation({ input, meta, ...props }: WrappedFieldProps) {
  const error = meta.touched && meta.error;
  return (
    <div className={error ? "has-error rd-ct-links__field" : "rd-ct-links__field"}>
      <Input {...input} {...props} />
    </div>
  );
}

function validateLink(value: string) {
  if (value !== "") {
    return false;
  }
  return true;
}
